import sum from 'root-core/utils/array/sum.js'
import {get} from 'lodash-es'
import roundMoney from '../price-engine/roundMoney.js'
import flatten from 'lowscore/flatten.js'
import extend from 'lowscore/extend.js'
import effectiveDiscount from '../price-engine/effectiveDiscount.js'
import absoluteDiscountAmount from './absoluteDiscountAmount.js'
import * as priceDiscounts from './priceDiscounts.js'
import * as roomOccupancy from '../accommodation/roomOccupancy.js'
import * as priceFTD from './priceFrequentTravellerDiscount.js'
import {applicableSupplementSelector} from './applicableSupplementReduction.js'

export function roomsWithBestCombinationOfDiscounts (
  combinability,
  {prices, requestedRooms, bookingDate, departure, perPassengerPrices}
) {
  const allDiscountsCombinable = {
    child: true,
    roomType: true,
    combinable: combinability.childDiscount && combinability.roomTypeDiscount,
  }

  const surcharges = prices.surcharges.filter(s =>
    effectiveDiscount({discount: s, bookingDate, departureDate: departure.startDate})
  )
  const frequentTravellerDiscount =  priceFTD.applicableFrequentTravellerDiscount(
    departure,
    bookingDate
  )

  const noGroupDiscountAvailable = !availableGroupDiscount(
    prices,
    requestedRooms,
    bookingDate
  )
  const roomsWithAnyPassengerDiscounts = breakdownRooms(
    requestedRooms,
    prices,
    allDiscountsCombinable,
    surcharges,
    frequentTravellerDiscount,
    departure,
    perPassengerPrices,
    bookingDate
  )

  if (noGroupDiscountAvailable) {
    return {rooms: roomsWithAnyPassengerDiscounts}
  } else if (
    combinability.groupDiscount &&
    combinability.childDiscount &&
    combinability.roomTypeDiscount
  ) {
    return roomsForAllDiscountsCombinable(
      roomsWithAnyPassengerDiscounts,
      prices,
      requestedRooms,
      bookingDate
    )
  } else if (combinability.groupDiscount && combinability.childDiscount) {
    return cheapestRoomsFor_GroupAndChild_Or_RoomType(
      roomsWithAnyPassengerDiscounts,
      bookingDate,
      requestedRooms,
      prices,
      surcharges,
      frequentTravellerDiscount,
      departure,
      perPassengerPrices
    )
  } else if (combinability.groupDiscount && combinability.roomTypeDiscount) {
    return cheapestRoomsFor_GroupAndRoomType_Or_Child(
      roomsWithAnyPassengerDiscounts,
      bookingDate,
      requestedRooms,
      prices,
      surcharges,
      frequentTravellerDiscount,
      departure,
      perPassengerPrices
    )
  } else {
    return cheapestRoomsFor_Group_Or_AnythingElse(
      roomsWithAnyPassengerDiscounts,
      bookingDate,
      requestedRooms,
      prices,
      surcharges,
      frequentTravellerDiscount,
      departure,
      perPassengerPrices
    )
  }
}

function breakdownRooms (
  requestedRooms,
  prices,
  combinabilityRule,
  surcharges,
  frequentTravellerDiscount,
  departure,
  perPassengerPrices,
  bookingDate
) {
  const {wasPrices} = departure
  return requestedRooms.map(room => {
    return {
      roomType: room.roomType,
      passengers: passengersWithApplicableDiscounts(
        departure,
        prices,
        room
      ).map(passenger => {
        return passengerQuote(
          room,
          combinabilityRule,
          passenger,
          surcharges,
          frequentTravellerDiscount,
          prices,
          wasPrices,
          perPassengerPrices,
          bookingDate
        )
      }),
    }
  })
}

export function passengersWithApplicableDiscounts (departure, prices, room) {
  const childrenWithAdultPrices = numberOfChildenWithAdultPrices(
    departure,
    prices,
    room
  )
  let numberOfChildren = 0

  return room.passengers.map(passenger => {
    const passengerWithApplicableDiscounts = extend({}, passenger, {
      basePrice: prices.baseRoomPrice,
      oldFullPrice: get(departure, 'wasPrices.baseRoomPrice', prices.baseRoomPrice),
      applicableChildDiscount: 0,
      applicableRoomTypeDiscount: 0,
      oldApplicableChildDiscount: 0,
      oldApplicableRoomTypeDiscount: 0,
    })

    const {wasPrices} = departure

    if (passenger.type == 'child') {
      numberOfChildren++
      const pricedAsChild = numberOfChildren > childrenWithAdultPrices
      if (pricedAsChild) {
        passengerWithApplicableDiscounts.oldApplicableChildDiscount = passengerWithApplicableDiscounts.applicableChildDiscount =
          get(prices, 'adjustments.discounts.child.value', 0)
      }
    }

    passengerWithApplicableDiscounts.applicableRoomTypeDiscount =
      get(prices, `adjustments.discounts.roomType.${room.roomType}`, 0)
    passengerWithApplicableDiscounts.applicableRoomTypeSupplement =
      get(prices, `adjustments.supplements.roomType.${room.roomType}`, 0)

    passengerWithApplicableDiscounts.oldApplicableRoomTypeDiscount =
      get(wasPrices, `adjustments.discounts.roomType.${room.roomType}`, 0)
    passengerWithApplicableDiscounts.oldApplicableRoomTypeSupplement =
      get(wasPrices, `adjustments.supplements.roomType.${room.roomType}`, 0)

    return passengerWithApplicableDiscounts
  })
}

function tooOldForChildDiscount (departure, child) {
  return child.age > departure.childAgeTo
}

function numberOfChildenWithAdultPrices (departure, prices, room) {
  const childDiscount = departure.prices.adjustments.discounts.child
  const passengersPricedAsAdults =
    roomOccupancy.numberOfAdultsInRoom(room) +
    roomOccupancy
      .childrenInRoom(room)
      .filter(child => tooOldForChildDiscount(departure, child)).length

  if (childDiscount) {
    const minOccupancy = childDiscount.minOccupantsBeforeDiscountApplies[room.roomType]
    const minOccupantsBeforeChildDiscountApplies = typeof minOccupancy === 'number'
      ? minOccupancy
      : room.passengers.length
    return (
      Math.max(
        passengersPricedAsAdults,
        minOccupantsBeforeChildDiscountApplies
      ) - roomOccupancy.numberOfAdultsInRoom(room)
    )
  } else {
    return 0
  }
}

function passengerQuote (
  room,
  combinabilityRule,
  passenger,
  surcharges,
  frequentTravellerDiscount,
  prices,
  wasPrices,
  perPassengerPrices,
  bookingDate
) {
  const miscProducts = []
  const roomPrice = calculatePassengerRoomPrice(
    room,
    combinabilityRule,
    passenger,
    surcharges,
    prices,
    perPassengerPrices
  )
  const wasRoomPrice = calculatePassengerRoomPrice(
    room,
    combinabilityRule,
    {
      ...passenger,
      basePrice: passenger.oldFullPrice,
      applicableChildDiscount: passenger.oldApplicableChildDiscount,
      applicableRoomTypeDiscount: passenger.oldApplicableRoomTypeDiscount,
      applicableRoomTypeSupplement: passenger.oldApplicableRoomTypeSupplement,
    },
    surcharges,
    {...prices, ...wasPrices},
    perPassengerPrices
  )

  let prepaidGratuitiesAmount = 0
  const prepaidGratuities = perPassengerPrices.prepaidGratuities(passenger)
  if (prepaidGratuities) {
    prepaidGratuitiesAmount = prepaidGratuities.amount
  }
  const mandatoryProducts = perPassengerPrices.mandatoryProducts(passenger)
  mandatoryProducts.forEach(p => {
    miscProducts.push({
      productName: p.productName,
      productCode: p.productCode,
      totalPrice: p.price,
      category: p.category,
      typeCode: p.typeCode,
    })
  })

  let availableDiscountAmount = 0
  let appliedDiscountAmount = 0
  if (frequentTravellerDiscount) {
    const {discountReduction, ftdApplicable} = applicableSupplementSelector({supplementAdjustments: prices.roomSupplementAdjustments, roomType: room.roomType, bookingDate})
    const basicRoomPrice = discountReduction && ftdApplicable
      ? roomPrice.basicRoomPrice - passenger.applicableRoomTypeSupplement
      : roomPrice.basicRoomPrice
    availableDiscountAmount = absoluteDiscountAmount(
      basicRoomPrice,
      frequentTravellerDiscount
    )
    if (passenger.frequentTravellerDiscount &&
        passenger.frequentTravellerDiscount.selected) {
      appliedDiscountAmount = availableDiscountAmount
      passenger.supplementsPromotions = [{
        isFTD: true,
        amount: Math.round(absoluteDiscountAmount(passenger.applicableRoomTypeSupplement, frequentTravellerDiscount)),
      }]
    }
  }

  return extend({}, passenger, {
    discounts: {
      frequentTravellerDiscount: passenger.frequentTravellerDiscount,
    },
    roomType: room.roomType,
    appliedDiscountAmount,
    roomPrice: roomPrice.totalRoomPrice,
    wasRoomPrice: wasRoomPrice.totalRoomPrice,
    basicRoomPrice: roomPrice.basicRoomPrice,
    wasBasicRoomPrice: wasRoomPrice.basicRoomPrice,
    surcharges: roomPrice.surcharges,
    foodFunds: roomPrice.foodFunds,
    taxes: roomPrice.taxes,
    miscProducts,
    travelProtectionProducts: [],
    total: roundMoney(
      roomPrice.totalRoomPrice + prepaidGratuitiesAmount - appliedDiscountAmount
    ),
    full: roundMoney(
      roomPrice.totalRoomPrice + prepaidGratuitiesAmount
    ),
    discountedBasicRoomPrice: roundMoney(
      roomPrice.basicRoomPrice - appliedDiscountAmount
    ),
    requestedPrepaidGratuities: prepaidGratuities,
    requestedTravelProtection: undefined,
    frequentTravellerDiscountAmount: availableDiscountAmount,
  })
}

const calculatePassengerRoomPrice = function (
  room,
  discounts,
  passenger,
  surcharges,
  prices,
  perPassengerPrices
) {

  if (prices.isWasPrices && !prices.isWasPrices.forRoomType[room.roomType]) { return {} }

  const childDiscount = discounts.child && passenger.applicableChildDiscount
  const roomTypeDiscount = discounts.roomType && passenger.applicableRoomTypeDiscount

  const discountsTotal = (passenger.applicableChildDiscount && !discounts.combinable) // It ignores discounts.child flag by design and uses passenger.applicableChildDiscount instead
    // ? Math.max(childDiscount, roomTypeDiscount) // Can't do that. Tropics implementation allows child price to be greater than adult price (BSP-2920)
    ? childDiscount
    : childDiscount + roomTypeDiscount

  const passengerRoomPrice = passenger.basePrice - discountsTotal

  const mandatoryProductsAmount = sum(
    perPassengerPrices.mandatoryProducts(passenger).map(p => p.price)
  )
  const mandatoryTravelProtectionAmount = perPassengerPrices.mandatoryTravelProtection()
    ? perPassengerPrices.mandatoryTravelProtection().price
    : 0
  const supplementAmount = prices.adjustments.supplements.roomType[room.roomType] || 0

  const basicRoomPrice = passengerRoomPrice + supplementAmount
  const passengerSurcharges = surcharges.map(s => {
    return extend({}, s, {
      amount: s.isPercentage
        ? roundMoney(basicRoomPrice * s.amount / 100)
        : s.amount,
    })
  })

  const totalRoomPrice =
    basicRoomPrice +
    mandatoryProductsAmount +
    mandatoryTravelProtectionAmount +
    perPassengerPrices.foodFunds() +
    sum(passengerSurcharges.map(s => s.amount)) +
    perPassengerPrices.taxes(passenger, basicRoomPrice)

  return {
    totalRoomPrice: roundMoney(totalRoomPrice),
    basicRoomPrice: roundMoney(basicRoomPrice),
    surcharges: passengerSurcharges,
    foodFunds: perPassengerPrices.foodFunds(),
    taxes: perPassengerPrices.allTaxes(passenger, basicRoomPrice),
  }
}

function roomsForAllDiscountsCombinable (
  roomsWithAnyPassengerDiscounts,
  prices,
  requestedRooms,
  bookingDate
) {
  return {
    rooms: roomsWithAnyPassengerDiscounts,
    groupDiscount: groupDiscountForRooms(
      roomsWithAnyPassengerDiscounts,
      prices,
      requestedRooms,
      bookingDate
    ),
  }
}

function cheapestRoomsFor_GroupAndChild_Or_RoomType (
  roomsWithAnyPassengerDiscounts,
  bookingDate,
  requestedRooms,
  prices,
  surcharges,
  frequentTravellerDiscount,
  departure,
  perPassengerPrices
) {
  const roomsWithAllDiscountsSubtotal = calculateRoomsSubtotal(
    roomsWithAnyPassengerDiscounts
  )
  const roomsWithChildDiscountOnly = breakdownRooms(
    requestedRooms,
    prices,
    {child: true, roomType: false},
    surcharges,
    frequentTravellerDiscount,
    departure,
    perPassengerPrices,
    bookingDate
  )
  const groupDiscount = groupDiscountForRooms(
    roomsWithChildDiscountOnly,
    prices,
    requestedRooms,
    bookingDate
  )

  if (
    calculateRoomsSubtotal(roomsWithChildDiscountOnly) - groupDiscount.amount <
    roomsWithAllDiscountsSubtotal
  ) {
    return {
      rooms: roomsWithChildDiscountOnly,
      groupDiscount,
    }
  } else {
    return {rooms: roomsWithAnyPassengerDiscounts}
  }
}

function cheapestRoomsFor_GroupAndRoomType_Or_Child (
  roomsWithAnyPassengerDiscounts,
  bookingDate,
  requestedRooms,
  prices,
  surcharges,
  frequentTravellerDiscount,
  departure,
  perPassengerPrices
) {
  const roomsWithAllDiscountsSubtotal = calculateRoomsSubtotal(
    roomsWithAnyPassengerDiscounts
  )
  const roomsWithRoomTypeDiscountOnly = breakdownRooms(
    requestedRooms,
    prices,
    {child: false, roomType: true},
    surcharges,
    frequentTravellerDiscount,
    departure,
    perPassengerPrices,
    bookingDate
  )
  const groupDiscount = groupDiscountForRooms(
    roomsWithRoomTypeDiscountOnly,
    prices,
    requestedRooms,
    bookingDate
  )

  if (
    calculateRoomsSubtotal(roomsWithRoomTypeDiscountOnly) -
      groupDiscount.amount <
    roomsWithAllDiscountsSubtotal
  ) {
    return {
      rooms: roomsWithRoomTypeDiscountOnly,
      groupDiscount,
    }
  } else {
    return {rooms: roomsWithAnyPassengerDiscounts}
  }
}

function cheapestRoomsFor_Group_Or_AnythingElse (
  roomsWithAnyPassengerDiscounts,
  bookingDate,
  requestedRooms,
  prices,
  surcharges,
  frequentTravellerDiscount,
  departure,
  perPassengerPrices
) {
  const roomsWithAllDiscountsSubtotal = calculateRoomsSubtotal(
    roomsWithAnyPassengerDiscounts
  )
  const roomsWithNoDiscounts = breakdownRooms(
    requestedRooms,
    prices,
    {child: false, roomType: false},
    surcharges,
    frequentTravellerDiscount,
    departure,
    perPassengerPrices,
    bookingDate
  )
  const groupDiscount = groupDiscountForRooms(
    roomsWithNoDiscounts,
    prices,
    requestedRooms,
    bookingDate
  )

  if (
    calculateRoomsSubtotal(roomsWithNoDiscounts) - groupDiscount.amount <
    roomsWithAllDiscountsSubtotal
  ) {
    return {
      rooms: roomsWithNoDiscounts,
      groupDiscount,
    }
  } else {
    return {rooms: roomsWithAnyPassengerDiscounts}
  }
}

function groupDiscountForRooms (rooms, prices, requestedRooms, bookingDate) {
  const totalBasicRoomPriceWithDiscounts = sum(
    flatten(rooms.map(room => room.passengers.map(p => p.basicRoomPrice)), true)
  )
  return applicableGroupDiscount(
    prices,
    totalBasicRoomPriceWithDiscounts,
    requestedRooms,
    bookingDate
  )
}

function applicableGroupDiscount (prices, base, requestedRooms, bookingDate) {
  const availableDiscount = availableGroupDiscount(
    prices,
    requestedRooms,
    bookingDate
  )

  if (availableDiscount) {
    return {
      name: availableDiscount.name,
      code: 'GDD',
      automatic: true,
      percent: Number(availableDiscount.percent),
      amount: absoluteDiscountAmount(
        base,
        availableDiscount,
        numberOfPassengers(requestedRooms)
      ),
    }
  }
}

function availableGroupDiscount (prices, requestedRooms, bookingDate) {
  const partySize = numberOfPassengers(requestedRooms)

  const available = (prices.adjustments.discounts.groupBookingDiscounts || []).filter(
    discount => {
      return (
        partySize >= discount.minGroupSize &&
        partySize <= discount.maxGroupSize &&
        effectiveDiscount({discount, bookingDate})
      )
    }
  )
  return priceDiscounts.mostRecentlyCreatedDiscount(available)
}

export function calculateRoomsSubtotal (rooms) {
  return sum(
    flatten(rooms.map(room => room.passengers.map(p => p.total)), true)
  )
}

function numberOfPassengersInRoom (room) {
  return room.passengers.length
}

function numberOfPassengers (rooms) {
  return sum(rooms.map(numberOfPassengersInRoom))
}